import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  Scissors,
  AudioLines,
  Contrast,
  Image as ImageIcon,
  Lock,
  Coins,
  RotateCcw,
  X,
  ChevronDown,
  Play,
  Check,
  Loader2,
} from 'lucide-react';
import {
  useVideoEditOps,
  type FinalizedVideoPatch,
  type AudioEnhanceMode,
  type ColorPreview,
  type VideoEditError,
  type OpResult,
  type VideoEditWorkingRef,
  type VideoEditOp,
} from '@/hooks/useVideoEditOps';
import { useSubscription } from '@/hooks/useSubscription';
import { useToast } from '@/hooks/use-toast';
import { useCreditConfirm } from '@/components/credits/CreditConfirmDialog';
import { UpgradeModal } from '@/components/subscription/UpgradeModal';

/**
 * VideoEditDrawer — the single "Edit video" surface for the inline video-edit
 * epic (Story 2). A right-anchored slide-out with four free-form tools (Trim,
 * Enhance Audio, Color, Background) usable in any order/subset, powered by the
 * `useVideoEditOps` op engine. Free users see a locked, upsell-only state; the
 * backend stays the hard gate.
 *
 * Recreated pixel-accurately from the design handoff (`Video Edit Drawer.dc.html`)
 * against the app's existing `--hj-*` token system. Two tokens the handoff
 * referenced are absent from index.css (`--hj-yellow-ink`, `--hj-green-tint`);
 * we inline their canonical values, matching the CreditConfirmDialog convention.
 */

// ---- Props ---------------------------------------------------------------

export interface VideoEditDrawerVideo {
  s3Key: string;
  url?: string;
  title?: string;
  muxPlaybackId?: string;
}

export interface VideoEditDrawerProps {
  open: boolean;
  video: VideoEditDrawerVideo;
  onApplied: (patch: FinalizedVideoPatch) => void;
  onClose: () => void;
}

// ---- Token shims (values absent from index.css) --------------------------

const YELLOW_INK = 'oklch(0.18 0.005 70)';
const GREEN_TINT = 'rgb(140 198 63 / 0.16)';

// ---- Tool model ----------------------------------------------------------

type ToolKey = 'trim' | 'audio' | 'color' | 'background';

/** UI tool key → the hook's op name (used to match busy/activeOp). */
const HOOK_OP: Record<ToolKey, VideoEditOp> = {
  trim: 'trim',
  audio: 'enhanceAudio',
  color: 'color',
  background: 'background',
};

const AUDIO_MODES: { key: AudioEnhanceMode; label: string; desc: string }[] = [
  { key: 'light', label: 'Light', desc: 'Gentle' },
  { key: 'natural', label: 'Natural', desc: 'Balanced' },
  { key: 'strong', label: 'Strong', desc: 'Podcast' },
];
const AUDIO_SUMMARY: Record<AudioEnhanceMode, string> = {
  light: 'Light cleanup',
  natural: 'Natural balance',
  strong: 'Podcast-grade',
};

// Fallback gradient swatches keyed by the real /api/filter/previews keys, used
// when a preview has no image thumbnail to render.
const PRESET_GRAD: Record<string, string> = {
  neutral: 'linear-gradient(135deg,#6c6c70,#3d3d40)',
  warm: 'linear-gradient(135deg,#d9a25e,#8a5a2c)',
  cool: 'linear-gradient(135deg,#6ea6d9,#2c4f7a)',
  cinema: 'linear-gradient(135deg,#2a3b4d,#c98f5a)',
  cinematic: 'linear-gradient(135deg,#2a3b4d,#c98f5a)',
  vivid: 'linear-gradient(135deg,#e05a8a,#5ab0e0)',
  mono: 'linear-gradient(135deg,#e8e8e8,#4a4a4a)',
  golden: 'linear-gradient(135deg,#f0c46a,#c97a2c)',
  matte: 'linear-gradient(135deg,#8a8f82,#4d5248)',
};

type SliderKey = 'brightness' | 'contrast' | 'saturation' | 'temperature' | 'exposure';
const SLIDER_DEFS: { key: SliderKey; label: string }[] = [
  { key: 'brightness', label: 'Brightness' },
  { key: 'contrast', label: 'Contrast' },
  { key: 'saturation', label: 'Saturation' },
  { key: 'temperature', label: 'Temperature' },
  { key: 'exposure', label: 'Exposure' },
];
const ZERO_SLIDERS: Record<SliderKey, number> = {
  brightness: 0,
  contrast: 0,
  saturation: 0,
  temperature: 0,
  exposure: 0,
};

type BgMode = 'keep' | 'replace' | 'green';
const BG_MODES: { key: BgMode; label: string }[] = [
  { key: 'keep', label: 'Keep' },
  { key: 'replace', label: 'Replace' },
  { key: 'green', label: 'Green screen' },
];
// key = stock-backgrounds/<key>.png filename passed to replaceBackground().
const BACKDROPS: { key: string; label: string; bg: string }[] = [
  { key: 'studio', label: 'Studio', bg: 'linear-gradient(135deg,#3a3a3f,#26262a)' },
  { key: 'warm', label: 'Warm set', bg: 'linear-gradient(135deg,#c9a36b,#8a6a3f)' },
  { key: 'blue', label: 'Deep blue', bg: 'linear-gradient(135deg,#2b3f6b,#16233f)' },
  { key: 'shelf', label: 'Bookshelf', bg: 'repeating-linear-gradient(90deg,#6b4f34 0 7px,#5a4029 7px 14px)' },
];

// ---- Helpers -------------------------------------------------------------

const fmt = (sec: number) => {
  if (!isFinite(sec) || sec < 0) sec = 0;
  const m = Math.floor(sec / 60);
  const s = Math.round(sec % 60);
  return `${m}:${String(s).padStart(2, '0')}`;
};

const buildWaveBars = () => {
  const bars: number[] = [];
  let seed = 7;
  for (let i = 0; i < 44; i++) {
    seed = (seed * 9301 + 49297) % 233280;
    bars.push(28 + Math.round((seed / 233280) * 68));
  }
  return bars;
};

// ---- Component -----------------------------------------------------------

export const VideoEditDrawer: React.FC<VideoEditDrawerProps> = ({ open, video, onApplied, onClose }) => {
  const { isSubscriber, tier, loading: subLoading } = useSubscription();
  const { toast } = useToast();
  const { confirmCredit, CreditConfirmDialog } = useCreditConfirm();

  // Plus gating: paid tools require an active subscription whose tier can use
  // tools. Creator tier is free/sell-only (no tools). Backend is the hard gate.
  const locked = !subLoading && (!isSubscriber || tier === 'creator');
  const isPlus = !locked;

  const editInput = useMemo(
    () => ({ s3Key: video.s3Key, url: video.url || `/api/s3/${video.s3Key}` }),
    [video.s3Key, video.url]
  );
  const ops = useVideoEditOps(editInput);
  const { busy, activeOp, currentUrl, revert, finalize } = ops;

  // ---- Local UI state ----
  const [expanded, setExpanded] = useState<ToolKey | null>(null);
  const [done, setDone] = useState<Record<ToolKey, boolean>>({
    trim: false,
    audio: false,
    color: false,
    background: false,
  });
  const [summary, setSummary] = useState<Record<ToolKey, string>>({
    trim: '',
    audio: '',
    color: '',
    background: '',
  });
  const [toolError, setToolError] = useState<Partial<Record<ToolKey, string>>>({});

  // Trim
  const [trimIn, setTrimIn] = useState(0.05);
  const [trimOut, setTrimOut] = useState(0.95);
  const [dragging, setDragging] = useState<'in' | 'out' | null>(null);
  const [playheadPct, setPlayheadPct] = useState<number | null>(null);
  const trackRef = useRef<HTMLDivElement | null>(null);

  // Audio
  const [audioMode, setAudioMode] = useState<AudioEnhanceMode>('natural');

  // Color
  const [colorTab, setColorTab] = useState<'presets' | 'custom'>('presets');
  const [colorPreset, setColorPreset] = useState<string | null>(null);
  const [previews, setPreviews] = useState<ColorPreview[]>([]);
  const [previewsLoaded, setPreviewsLoaded] = useState(false);
  const [previewsLoading, setPreviewsLoading] = useState(false);
  const [sliders, setSliders] = useState<Record<SliderKey, number>>({ ...ZERO_SLIDERS });

  // Background
  const [bgMode, setBgMode] = useState<BgMode>('keep');
  const [backdrop, setBackdrop] = useState<string | null>(null);

  // Preview player
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [duration, setDuration] = useState(60);
  const [resLabel, setResLabel] = useState('');
  const [playing, setPlaying] = useState(false);

  const [showUpgrade, setShowUpgrade] = useState(false);

  // Entrance animation: hosts mount the drawer with `open` already true, so
  // gate the visible state one frame behind `open` to let the translate/scrim
  // transitions play.
  const [entered, setEntered] = useState(false);
  useEffect(() => {
    if (open) {
      const id = requestAnimationFrame(() => setEntered(true));
      return () => cancelAnimationFrame(id);
    }
    setEntered(false);
  }, [open]);
  const shown = open && entered;

  const waveBars = useMemo(() => buildWaveBars(), []);

  // Reset all local UI state whenever the drawer is (re)opened on a video.
  const resetLocal = useCallback(() => {
    setExpanded(null);
    setDone({ trim: false, audio: false, color: false, background: false });
    setSummary({ trim: '', audio: '', color: '', background: '' });
    setToolError({});
    setTrimIn(0.05);
    setTrimOut(0.95);
    setDragging(null);
    setAudioMode('natural');
    setColorTab('presets');
    setColorPreset(null);
    setSliders({ ...ZERO_SLIDERS });
    setBgMode('keep');
    setBackdrop(null);
  }, []);

  useEffect(() => {
    if (open) resetLocal();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, video.s3Key]);

  // ---- Trim drag (pointer events on window, 5% min gap) ----
  useEffect(() => {
    if (!dragging) return;
    const onMove = (e: PointerEvent) => {
      const el = trackRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      let p = (e.clientX - r.left) / r.width;
      p = Math.max(0, Math.min(1, p));
      let next: number;
      if (dragging === 'in') {
        next = Math.min(p, trimOut - 0.05);
        setTrimIn(next);
      } else {
        next = Math.max(p, trimIn + 0.05);
        setTrimOut(next);
      }
      // Scrub the preview to the handle so you can see the exact frame.
      const v = videoRef.current;
      if (v && isFinite(v.duration)) v.currentTime = next * v.duration;
    };
    const onUp = () => setDragging(null);
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
    };
  }, [dragging, trimIn, trimOut]);

  // ---- Lazy-load color previews the first time Color expands ----
  useEffect(() => {
    if (!isPlus) return;
    if (expanded !== 'color' || previewsLoaded || previewsLoading) return;
    setPreviewsLoading(true);
    ops
      .colorPreviews()
      .then((res: OpResult<ColorPreview[]>) => {
        if (res.ok) setPreviews(res.data);
        setPreviewsLoaded(true);
      })
      .finally(() => setPreviewsLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expanded, isPlus, previewsLoaded, previewsLoading]);

  // ---- Derived ----
  const editCount = (Object.keys(done) as ToolKey[]).filter((k) => done[k]).length;
  const anyEdit = editCount > 0;

  const spinning = (tool: ToolKey) => busy && activeOp === HOOK_OP[tool];

  const markDone = useCallback((tool: ToolKey, text: string) => {
    setDone((d) => ({ ...d, [tool]: true }));
    setSummary((s) => ({ ...s, [tool]: text }));
    setToolError((e) => ({ ...e, [tool]: undefined }));
    setExpanded(null);
  }, []);

  // Route a failed OpResult to the right upsell/error surface per the contract.
  const handleFailure = useCallback(
    (tool: ToolKey, error: VideoEditError, retryConfirm?: () => void) => {
      if (error.code === 'INSUFFICIENT_CREDITS') {
        // Re-open the confirm dialog; the shared component flips to its buy state.
        retryConfirm?.();
        return;
      }
      if (error.code === 'PLUS_REQUIRED') {
        setShowUpgrade(true);
        toast({ title: 'Hey Joe Plus required', description: 'Editing tools live in Plus.', variant: 'destructive' });
        return;
      }
      setToolError((e) => ({ ...e, [tool]: error.message }));
      toast({ title: 'Edit failed', description: error.message, variant: 'destructive' });
    },
    [toast]
  );

  const settle = useCallback(
    (tool: ToolKey, res: OpResult<VideoEditWorkingRef>, label: string, retryConfirm?: () => void) => {
      if (res.ok) markDone(tool, label);
      else handleFailure(tool, res.error, retryConfirm);
    },
    [markDone, handleFailure]
  );

  const toggle = useCallback(
    (tool: ToolKey) => {
      if (!isPlus || busy) return;
      setExpanded((cur) => (cur === tool ? null : tool));
    },
    [isPlus, busy]
  );

  // ---- Tool actions ----
  const runTrim = useCallback(async () => {
    const res = await ops.trim(trimIn * duration, trimOut * duration);
    settle('trim', res, `${fmt(trimIn * duration)}–${fmt(trimOut * duration)}`);
  }, [ops, trimIn, trimOut, duration, settle]);

  const askAudio = useCallback(() => {
    const run = async () => {
      const res = await ops.enhanceAudio(audioMode);
      settle('audio', res, AUDIO_SUMMARY[audioMode], askAudio);
    };
    confirmCredit('audio_enhancement', 'Enhance Audio', run);
  }, [ops, audioMode, settle, confirmCredit]);

  const runColor = useCallback(async () => {
    if (colorTab === 'presets') {
      if (!colorPreset) {
        toast({ title: 'Pick a grade first' });
        return;
      }
      const label = previews.find((p) => p.key === colorPreset)?.label || colorPreset;
      const res = await ops.applyColor(colorPreset);
      settle('color', res, label);
    } else {
      const changed = SLIDER_DEFS.some((d) => sliders[d.key] !== 0);
      if (!changed) {
        toast({ title: 'Adjust a slider first' });
        return;
      }
      const res = await ops.applyColor(sliders);
      settle('color', res, 'Custom grade');
    }
  }, [colorTab, colorPreset, previews, sliders, ops, settle, toast]);

  const applyBgKeep = useCallback(() => {
    markDone('background', 'Original kept');
  }, [markDone]);

  const askBackground = useCallback(() => {
    const isGreen = bgMode === 'green';
    const opType = isGreen ? 'background_removal' : 'composite_background';
    const opName = isGreen ? 'Green screen' : 'Replace background';
    const run = async () => {
      const res = isGreen
        ? await ops.replaceBackground({ mode: 'green-screen' })
        : await ops.replaceBackground({ mode: 'backdrop', backdrop: backdrop || undefined });
      const label = isGreen ? 'Green screen' : BACKDROPS.find((d) => d.key === backdrop)?.label || 'Studio';
      settle('background', res, label, askBackground);
    };
    confirmCredit(opType, opName, run);
  }, [bgMode, backdrop, ops, settle, confirmCredit]);

  // ---- Footer actions ----
  const handleRevert = useCallback(() => {
    revert();
    resetLocal();
  }, [revert, resetLocal]);

  const handleCancel = useCallback(() => {
    revert();
    resetLocal();
    onClose();
  }, [revert, resetLocal, onClose]);

  const handleApply = useCallback(async () => {
    if (!anyEdit || busy) return;
    const res = await finalize();
    if (res.ok) {
      onApplied(res.data);
      onClose();
      toast({ title: 'Edits applied to the lesson' });
    } else {
      handleFailure('trim', res.error);
    }
  }, [anyEdit, busy, finalize, onApplied, onClose, toast, handleFailure]);

  // ---- Preview player ----
  const onLoadedMeta = useCallback(() => {
    const v = videoRef.current;
    if (!v) return;
    if (isFinite(v.duration) && v.duration > 0) setDuration(v.duration);
    const h = v.videoHeight;
    if (h >= 2160) setResLabel('2160p');
    else if (h >= 1080) setResLabel('1080p');
    else if (h >= 720) setResLabel('720p');
    else if (h > 0) setResLabel(`${h}p`);
  }, []);

  const togglePlay = useCallback(() => {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) v.play().catch(() => undefined);
    else v.pause();
  }, []);

  const metaText = resLabel ? `${fmt(duration)} · ${resLabel}` : fmt(duration);

  // ---- Style helpers ----
  const rowStyle = (tool: ToolKey, isOpen: boolean) => {
    const isDone = done[tool];
    return {
      border: isDone ? 'var(--hj-green)' : isOpen ? 'var(--hj-yellow)' : 'var(--hj-hairline)',
      iconBg: isDone ? GREEN_TINT : isOpen ? 'var(--hj-yellow-tint)' : 'var(--hj-surface-3)',
      iconFg: isDone ? 'var(--hj-green)' : isOpen ? 'var(--hj-yellow)' : 'var(--hj-ink-3)',
      descColor: isDone ? 'var(--hj-green)' : 'var(--hj-ink-4)',
    };
  };

  const inPct = trimIn * 100;
  const outPct = trimOut * 100;

  // ---- Sub-renderers ----
  const renderRowHeader = (
    tool: ToolKey,
    icon: React.ReactNode,
    title: string,
    idleDesc: string,
    doneVerb: string,
    rightIdle: React.ReactNode
  ) => {
    const isOpen = isPlus && expanded === tool;
    const st = rowStyle(tool, isOpen);
    const desc = done[tool] ? `${doneVerb} · ${summary[tool]}` : idleDesc;
    return (
      <button
        type="button"
        onClick={() => toggle(tool)}
        style={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          gap: 13,
          padding: '14px 15px',
          background: 'transparent',
          border: 'none',
          cursor: isPlus ? 'pointer' : 'default',
          textAlign: 'left',
          color: 'inherit',
          fontFamily: 'inherit',
        }}
      >
        <span
          style={{
            width: 38,
            height: 38,
            flex: 'none',
            borderRadius: 10,
            background: st.iconBg,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: st.iconFg,
          }}
        >
          {icon}
        </span>
        <span style={{ flex: 1, minWidth: 0 }}>
          <span style={{ display: 'block', fontSize: 14.5, fontWeight: 600, color: 'var(--hj-ink)' }}>{title}</span>
          <span style={{ display: 'block', fontSize: 12, color: st.descColor, marginTop: 2 }}>{desc}</span>
        </span>
        {isPlus && !done[tool] && !isOpen ? rightIdle : null}
        {locked ? (
          <span style={{ color: 'var(--hj-ink-4)' }}>
            <Lock size={16} strokeWidth={2} />
          </span>
        ) : null}
        {isPlus ? (
          <span
            style={{
              color: 'var(--hj-ink-4)',
              transform: `rotate(${isOpen ? 180 : 0}deg)`,
              transition: 'transform .2s',
              display: 'inline-flex',
            }}
          >
            <ChevronDown size={18} strokeWidth={2} />
          </span>
        ) : null}
      </button>
    );
  };

  const creditChip = (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
        padding: '3px 8px',
        borderRadius: 999,
        background: 'var(--hj-surface-3)',
        border: '1px solid var(--hj-hairline)',
        color: 'var(--hj-ink-3)',
        fontSize: 11,
        fontWeight: 700,
      }}
    >
      <Coins size={11} strokeWidth={2} />1
    </span>
  );

  const freeChip = (
    <span
      style={{
        padding: '3px 8px',
        borderRadius: 999,
        background: GREEN_TINT,
        color: 'var(--hj-green)',
        fontSize: 10.5,
        fontWeight: 700,
        letterSpacing: '0.03em',
      }}
    >
      FREE
    </span>
  );

  const secondaryBtn: React.CSSProperties = {
    width: '100%',
    height: 40,
    borderRadius: 10,
    border: '1px solid var(--hj-hairline)',
    background: 'var(--hj-surface-3)',
    color: 'var(--hj-ink)',
    fontFamily: 'inherit',
    fontSize: 13,
    fontWeight: 700,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 7,
  };
  const primaryBtn: React.CSSProperties = {
    width: '100%',
    height: 40,
    borderRadius: 10,
    border: 'none',
    background: 'var(--hj-yellow)',
    color: YELLOW_INK,
    fontFamily: 'inherit',
    fontSize: 13,
    fontWeight: 700,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 7,
  };
  const Spin = () => <Loader2 size={15} strokeWidth={2.5} style={{ animation: 'hj-spin 1s linear infinite' }} />;

  const toolErr = (tool: ToolKey) =>
    toolError[tool] ? (
      <p style={{ margin: '9px 2px 0', fontSize: 11.5, color: 'hsl(var(--destructive))', textAlign: 'center' }}>
        {toolError[tool]}
      </p>
    ) : null;

  // Portal to <body>: hosts may mount the drawer inside transformed ancestors
  // (e.g. a Radix DialogContent), which would otherwise re-anchor position:fixed.
  return createPortal(
    <div
      aria-hidden={!open}
      style={{ position: 'fixed', inset: 0, zIndex: 60, pointerEvents: open ? 'auto' : 'none' }}
    >
      {/* keyframes for the spinner (scoped, harmless if duplicated) */}
      <style>{`@keyframes hj-spin{to{transform:rotate(360deg)}}`}</style>

      {/* scrim */}
      <div
        onClick={onClose}
        style={{
          position: 'absolute',
          inset: 0,
          background: `rgba(8,7,6,${shown ? 0.55 : 0})`,
          transition: 'background .28s ease',
          pointerEvents: open ? 'auto' : 'none',
        }}
      />

      {/* drawer */}
      <aside
        role="dialog"
        aria-modal="true"
        style={{
          position: 'absolute',
          top: 0,
          right: 0,
          height: '100%',
          width: 452,
          maxWidth: '94vw',
          background: 'var(--hj-surface-1)',
          borderLeft: '1px solid var(--hj-hairline)',
          boxShadow: '-24px 0 64px rgba(0,0,0,.45)',
          display: 'flex',
          flexDirection: 'column',
          transform: `translateX(${shown ? '0' : '110%'})`,
          transition: 'transform .32s cubic-bezier(.4,0,.2,1)',
          color: 'var(--hj-ink)',
        }}
      >
        {/* ---- header ---- */}
        <div style={{ flex: 'none', padding: '20px 22px 16px', borderBottom: '1px solid var(--hj-hairline-soft)' }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div className="mono-label">Edit video</div>
              <h2
                className="font-hj-display"
                style={{ fontWeight: 700, fontSize: 21, letterSpacing: '-0.02em', margin: '3px 0 0', color: 'var(--hj-ink)' }}
              >
                {video.title || 'Untitled lesson'}
              </h2>
            </div>
            <button
              type="button"
              onClick={onClose}
              aria-label="Close"
              style={{
                width: 34,
                height: 34,
                flex: 'none',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: 9,
                border: '1px solid var(--hj-hairline)',
                background: 'transparent',
                color: 'var(--hj-ink-3)',
                cursor: 'pointer',
              }}
            >
              <X size={16} strokeWidth={2} />
            </button>
          </div>

          {/* preview */}
          <div
            style={{
              marginTop: 14,
              position: 'relative',
              aspectRatio: '16 / 9',
              borderRadius: 12,
              overflow: 'hidden',
              background: 'repeating-linear-gradient(135deg,#242428 0 10px,#1e1e21 10px 20px)',
              border: '1px solid var(--hj-hairline-soft)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {currentUrl ? (
              <video
                ref={videoRef}
                onTimeUpdate={(e) => {
                  const v = e.currentTarget;
                  if (isFinite(v.duration) && v.duration > 0) setPlayheadPct(v.currentTime / v.duration);
                }}
                src={currentUrl}
                playsInline
                onLoadedMetadata={onLoadedMeta}
                onPlay={() => setPlaying(true)}
                onPause={() => setPlaying(false)}
                onEnded={() => setPlaying(false)}
                style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', background: '#000' }}
              />
            ) : null}
            
            {!playing ? (
              <button
                type="button"
                onClick={togglePlay}
                aria-label="Play preview"
                style={{
                  position: 'relative',
                  width: 52,
                  height: 52,
                  borderRadius: '50%',
                  background: 'rgba(255,255,255,.12)',
                  backdropFilter: 'blur(4px)',
                  border: '1px solid rgba(255,255,255,.25)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  color: 'var(--hj-ink)',
                }}
              >
                <Play size={20} fill="currentColor" stroke="none" style={{ marginLeft: 2 }} />
              </button>
            ) : (
              <button
                type="button"
                onClick={togglePlay}
                aria-label="Pause preview"
                style={{ position: 'absolute', inset: 0, background: 'transparent', border: 'none', cursor: 'pointer' }}
              />
            )}
            <span
              className="mono-label tabular"
              style={{ position: 'absolute', bottom: 10, right: 12, color: 'var(--hj-ink-2)', pointerEvents: 'none' }}
            >
              {metaText}
            </span>
          </div>
        </div>

        {/* ---- body ---- */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '16px 18px', opacity: locked ? 0.62 : 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', margin: '2px 4px 12px' }}>
            <span className="mono-label">Tools</span>
            {isPlus ? (
              <span style={{ fontSize: 11.5, color: 'var(--hj-ink-4)' }}>Use any, in any order</span>
            ) : (
              <span
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 5,
                  fontSize: 11,
                  fontWeight: 700,
                  color: 'var(--hj-pink)',
                }}
              >
                <Lock size={12} strokeWidth={2} />
                FREE PLAN
              </span>
            )}
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {/* ===== TRIM ===== */}
            <div
              style={{
                border: `1px solid ${rowStyle('trim', isPlus && expanded === 'trim').border}`,
                borderRadius: 13,
                background: 'var(--hj-surface-2)',
                overflow: 'hidden',
              }}
            >
              {renderRowHeader(
                'trim',
                <Scissors size={18} strokeWidth={2} />,
                'Trim',
                'Set start and end points',
                'Trimmed',
                null
              )}
              {isPlus && expanded === 'trim' ? (
                <div style={{ padding: '4px 15px 17px' }}>
                  <div
                    ref={trackRef}
                    style={{
                      position: 'relative',
                      height: 74,
                      borderRadius: 9,
                      background: 'var(--hj-bg)',
                      border: '1px solid var(--hj-hairline-soft)',
                      overflow: 'hidden',
                    }}
                  >
                    <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', gap: 2, padding: '0 2px' }}>
                      {waveBars.map((h, i) => (
                        <span
                          key={i}
                          style={{ flex: 1, height: `${h}%`, background: 'var(--hj-ink-4)', opacity: 0.4, borderRadius: 1 }}
                        />
                      ))}
                    </div>
                    <div style={{ position: 'absolute', top: 0, bottom: 0, left: 0, width: `${inPct}%`, background: 'rgba(14,13,12,.7)' }} />
                    <div style={{ position: 'absolute', top: 0, bottom: 0, right: 0, width: `${100 - outPct}%`, background: 'rgba(14,13,12,.7)' }} />
                    <div
                      style={{
                        position: 'absolute',
                        top: 0,
                        bottom: 0,
                        left: `${inPct}%`,
                        width: `${outPct - inPct}%`,
                        border: '1.5px solid var(--hj-yellow)',
                        background: 'var(--hj-yellow-tint)',
                      }}
                    />
            {playheadPct !== null && playheadPct >= 0 && playheadPct <= 1 && (
              <div
                aria-hidden
                style={{
                  position: 'absolute',
                  top: 0,
                  bottom: 0,
                  left: `${playheadPct * 100}%`,
                  width: 2,
                  background: 'var(--hj-ink)',
                  opacity: 0.75,
                  pointerEvents: 'none',
                }}
              />
            )}
                    <div
                      onPointerDown={() => setDragging('in')}
                      style={{
                        position: 'absolute',
                        top: 0,
                        bottom: 0,
                        left: `${inPct}%`,
                        width: 11,
                        marginLeft: -5.5,
                        background: 'var(--hj-yellow)',
                        borderRadius: 4,
                        cursor: 'ew-resize',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        touchAction: 'none',
                      }}
                    >
                      <span style={{ width: 2, height: 16, background: YELLOW_INK, opacity: 0.5, borderRadius: 2 }} />
                    </div>
                    <div
                      onPointerDown={() => setDragging('out')}
                      style={{
                        position: 'absolute',
                        top: 0,
                        bottom: 0,
                        left: `${outPct}%`,
                        width: 11,
                        marginLeft: -5.5,
                        background: 'var(--hj-yellow)',
                        borderRadius: 4,
                        cursor: 'ew-resize',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        touchAction: 'none',
                      }}
                    >
                      <span style={{ width: 2, height: 16, background: YELLOW_INK, opacity: 0.5, borderRadius: 2 }} />
                    </div>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 10 }}>
                    <div>
                      <span className="mono-label">In</span>
                      <div className="tabular" style={{ fontFamily: 'var(--hj-font-mono)', fontSize: 14, fontWeight: 500, color: 'var(--hj-ink)', marginTop: 1 }}>
                        {fmt(trimIn * duration)}
                      </div>
                    </div>
                    <div style={{ textAlign: 'center' }}>
                      <span className="mono-label">Selected</span>
                      <div className="tabular" style={{ fontFamily: 'var(--hj-font-mono)', fontSize: 14, fontWeight: 500, color: 'var(--hj-yellow-ink)', marginTop: 1 }}>
                        {fmt((trimOut - trimIn) * duration)}
                      </div>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <span className="mono-label">Out</span>
                      <div className="tabular" style={{ fontFamily: 'var(--hj-font-mono)', fontSize: 14, fontWeight: 500, color: 'var(--hj-ink)', marginTop: 1 }}>
                        {fmt(trimOut * duration)}
                      </div>
                    </div>
                  </div>
                  <button type="button" onClick={runTrim} disabled={busy} style={{ ...secondaryBtn, marginTop: 14, opacity: busy ? 0.7 : 1 }}>
                    {spinning('trim') ? (
                      <>
                        <Spin /> Trimming…
                      </>
                    ) : (
                      'Set trim points'
                    )}
                  </button>
                  {toolErr('trim')}
                </div>
              ) : null}
            </div>

            {/* ===== ENHANCE AUDIO ===== */}
            <div
              style={{
                border: `1px solid ${rowStyle('audio', isPlus && expanded === 'audio').border}`,
                borderRadius: 13,
                background: 'var(--hj-surface-2)',
                overflow: 'hidden',
              }}
            >
              {renderRowHeader(
                'audio',
                <AudioLines size={18} strokeWidth={2} />,
                'Enhance Audio',
                'Reduce noise, balance levels',
                'Enhanced',
                creditChip
              )}
              {isPlus && expanded === 'audio' ? (
                <div style={{ padding: '2px 15px 17px' }}>
                  <div style={{ display: 'flex', gap: 8 }}>
                    {AUDIO_MODES.map((m) => {
                      const on = audioMode === m.key;
                      return (
                        <button
                          key={m.key}
                          type="button"
                          onClick={() => setAudioMode(m.key)}
                          style={{
                            flex: 1,
                            padding: '10px 6px',
                            borderRadius: 10,
                            border: `1.5px solid ${on ? 'var(--hj-yellow)' : 'var(--hj-hairline)'}`,
                            background: on ? 'var(--hj-yellow-tint)' : 'transparent',
                            color: on ? 'var(--hj-yellow)' : 'var(--hj-ink-2)',
                            cursor: 'pointer',
                            fontFamily: 'inherit',
                          }}
                        >
                          <span style={{ display: 'block', fontSize: 13, fontWeight: 700 }}>{m.label}</span>
                          <span style={{ display: 'block', fontSize: 10.5, marginTop: 2, opacity: 0.8 }}>{m.desc}</span>
                        </button>
                      );
                    })}
                  </div>
                  <button type="button" onClick={askAudio} disabled={busy} style={{ ...primaryBtn, marginTop: 13, opacity: busy ? 0.7 : 1 }}>
                    {spinning('audio') ? (
                      <>
                        <Spin /> Enhancing…
                      </>
                    ) : (
                      <>
                        Enhance <span style={{ opacity: 0.7, fontWeight: 600 }}>· 1 video token</span>
                      </>
                    )}
                  </button>
                  {toolErr('audio')}
                </div>
              ) : null}
            </div>

            {/* ===== COLOR ===== */}
            <div
              style={{
                border: `1px solid ${rowStyle('color', isPlus && expanded === 'color').border}`,
                borderRadius: 13,
                background: 'var(--hj-surface-2)',
                overflow: 'hidden',
              }}
            >
              {renderRowHeader(
                'color',
                <Contrast size={18} strokeWidth={2} />,
                'Color',
                'Presets or manual grade',
                'Graded',
                freeChip
              )}
              {isPlus && expanded === 'color' ? (
                <div style={{ padding: '2px 15px 17px' }}>
                  <div
                    style={{
                      display: 'inline-flex',
                      padding: 3,
                      borderRadius: 9,
                      background: 'var(--hj-bg)',
                      border: '1px solid var(--hj-hairline-soft)',
                      marginBottom: 13,
                    }}
                  >
                    {(['presets', 'custom'] as const).map((tab) => {
                      const on = colorTab === tab;
                      return (
                        <button
                          key={tab}
                          type="button"
                          onClick={() => setColorTab(tab)}
                          style={{
                            padding: '6px 16px',
                            borderRadius: 7,
                            border: 'none',
                            fontFamily: 'inherit',
                            fontSize: 12.5,
                            fontWeight: 700,
                            cursor: 'pointer',
                            background: on ? 'var(--hj-surface-3)' : 'transparent',
                            color: on ? 'var(--hj-ink)' : 'var(--hj-ink-4)',
                            textTransform: 'capitalize',
                          }}
                        >
                          {tab}
                        </button>
                      );
                    })}
                  </div>

                  {colorTab === 'presets' ? (
                    previewsLoading && previews.length === 0 ? (
                      <p style={{ margin: '4px 2px', fontSize: 12, color: 'var(--hj-ink-4)', textAlign: 'center' }}>
                        Loading grades…
                      </p>
                    ) : previews.length === 0 ? (
                      <p style={{ margin: '4px 2px', fontSize: 12, color: 'var(--hj-ink-4)', textAlign: 'center' }}>
                        Presets unavailable — use Custom.
                      </p>
                    ) : (
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 8 }}>
                        {previews.map((p) => {
                          const on = colorPreset === p.key;
                          const grad = PRESET_GRAD[p.key] || 'linear-gradient(135deg,#6c6c70,#3d3d40)';
                          const hasImg = !!p.preview && /^(https?:|\/|data:)/.test(p.preview);
                          return (
                            <button
                              key={p.key}
                              type="button"
                              onClick={() => setColorPreset(p.key)}
                              style={{
                                border: `1.5px solid ${on ? 'var(--hj-yellow)' : 'var(--hj-hairline-soft)'}`,
                                borderRadius: 9,
                                overflow: 'hidden',
                                background: 'transparent',
                                cursor: 'pointer',
                                padding: 0,
                              }}
                            >
                              <span
                                style={{
                                  display: 'block',
                                  height: 38,
                                  background: hasImg ? `center/cover no-repeat url(${p.preview}), ${grad}` : grad,
                                }}
                              />
                              <span
                                style={{
                                  display: 'block',
                                  fontSize: 10.5,
                                  fontWeight: 600,
                                  color: on ? 'var(--hj-ink)' : 'var(--hj-ink-3)',
                                  padding: '5px 2px',
                                }}
                              >
                                {p.label}
                              </span>
                            </button>
                          );
                        })}
                      </div>
                    )
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 13 }}>
                      {SLIDER_DEFS.map((d) => {
                        const v = sliders[d.key];
                        const pct = (v + 100) / 2;
                        const fillLeft = Math.min(50, pct);
                        const fillWidth = Math.abs(50 - pct);
                        return (
                          <div key={d.key}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                              <span style={{ fontSize: 12, color: 'var(--hj-ink-2)' }}>{d.label}</span>
                              <span className="tabular" style={{ fontFamily: 'var(--hj-font-mono)', fontSize: 11.5, color: 'var(--hj-ink-3)' }}>
                                {v > 0 ? '+' : ''}
                                {v}
                              </span>
                            </div>
                            <div style={{ position: 'relative', height: 22, display: 'flex', alignItems: 'center' }}>
                              <div style={{ position: 'absolute', left: 0, right: 0, height: 4, borderRadius: 999, background: 'var(--hj-surface-3)' }} />
                              <div style={{ position: 'absolute', height: 4, borderRadius: 999, background: 'var(--hj-yellow)', left: `${fillLeft}%`, width: `${fillWidth}%` }} />
                              <div
                                style={{
                                  position: 'absolute',
                                  width: 14,
                                  height: 14,
                                  borderRadius: '50%',
                                  background: 'var(--hj-yellow)',
                                  border: '2px solid var(--hj-surface-2)',
                                  left: `${pct}%`,
                                  marginLeft: -7,
                                  pointerEvents: 'none',
                                }}
                              />
                              <input
                                type="range"
                                min={-100}
                                max={100}
                                value={v}
                                onChange={(e) => setSliders((s) => ({ ...s, [d.key]: Number(e.target.value) }))}
                                aria-label={d.label}
                                style={{
                                  position: 'absolute',
                                  left: 0,
                                  right: 0,
                                  width: '100%',
                                  height: 22,
                                  margin: 0,
                                  opacity: 0,
                                  cursor: 'pointer',
                                }}
                              />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}

                  <button type="button" onClick={runColor} disabled={busy} style={{ ...secondaryBtn, marginTop: 15, opacity: busy ? 0.7 : 1 }}>
                    {spinning('color') ? (
                      <>
                        <Spin /> Grading…
                      </>
                    ) : (
                      'Apply color'
                    )}
                  </button>
                  <p style={{ margin: '9px 2px 0', fontSize: 11, color: 'var(--hj-ink-4)', textAlign: 'center' }}>
                    Color grading is free — no video tokens.
                  </p>
                  {toolErr('color')}
                </div>
              ) : null}
            </div>

            {/* ===== BACKGROUND ===== */}
            <div
              style={{
                border: `1px solid ${rowStyle('background', isPlus && expanded === 'background').border}`,
                borderRadius: 13,
                background: 'var(--hj-surface-2)',
                overflow: 'hidden',
              }}
            >
              {renderRowHeader(
                'background',
                <ImageIcon size={18} strokeWidth={2} />,
                'Background',
                'Keep, replace, or green-screen',
                'Applied',
                creditChip
              )}
              {isPlus && expanded === 'background' ? (
                <div style={{ padding: '2px 15px 17px' }}>
                  <div style={{ display: 'flex', gap: 8 }}>
                    {BG_MODES.map((m) => {
                      const on = bgMode === m.key;
                      return (
                        <button
                          key={m.key}
                          type="button"
                          onClick={() => setBgMode(m.key)}
                          style={{
                            flex: 1,
                            padding: '10px 6px',
                            borderRadius: 10,
                            border: `1.5px solid ${on ? 'var(--hj-yellow)' : 'var(--hj-hairline)'}`,
                            background: on ? 'var(--hj-yellow-tint)' : 'transparent',
                            color: on ? 'var(--hj-yellow)' : 'var(--hj-ink-2)',
                            cursor: 'pointer',
                            fontFamily: 'inherit',
                          }}
                        >
                          <span style={{ display: 'block', fontSize: 12.5, fontWeight: 700 }}>{m.label}</span>
                        </button>
                      );
                    })}
                  </div>

                  {bgMode === 'replace' ? (
                    <div style={{ marginTop: 13 }}>
                      <span className="mono-label" style={{ display: 'block', marginBottom: 8 }}>
                        Choose a backdrop
                      </span>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 8 }}>
                        {BACKDROPS.map((d) => {
                          const on = backdrop === d.key;
                          return (
                            <button
                              key={d.key}
                              type="button"
                              onClick={() => setBackdrop(d.key)}
                              style={{
                                border: `1.5px solid ${on ? 'var(--hj-yellow)' : 'var(--hj-hairline-soft)'}`,
                                borderRadius: 9,
                                overflow: 'hidden',
                                padding: 0,
                                cursor: 'pointer',
                                background: 'transparent',
                              }}
                            >
                              <span style={{ display: 'block', height: 40, background: d.bg }} />
                              <span
                                style={{
                                  display: 'block',
                                  fontSize: 10,
                                  fontWeight: 600,
                                  color: on ? 'var(--hj-ink)' : 'var(--hj-ink-3)',
                                  padding: '4px 2px',
                                }}
                              >
                                {d.label}
                              </span>
                            </button>
                          );
                        })}
                        <button
                          type="button"
                          disabled
                          title="Upload coming soon"
                          style={{
                            border: '1.5px dashed var(--hj-hairline)',
                            borderRadius: 9,
                            background: 'transparent',
                            cursor: 'not-allowed',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: 3,
                            color: 'var(--hj-ink-4)',
                            minHeight: 63,
                          }}
                        >
                          <span style={{ fontSize: 18, lineHeight: 1 }}>+</span>
                          <span style={{ fontSize: 9.5, fontWeight: 600 }}>Upload</span>
                        </button>
                      </div>
                    </div>
                  ) : null}

                  {bgMode === 'keep' ? (
                    <button type="button" onClick={applyBgKeep} disabled={busy} style={{ ...secondaryBtn, marginTop: 14 }}>
                      Keep original background
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={askBackground}
                      disabled={busy || (bgMode === 'replace' && !backdrop)}
                      style={{
                        ...primaryBtn,
                        marginTop: 14,
                        opacity: busy || (bgMode === 'replace' && !backdrop) ? 0.55 : 1,
                        cursor: busy || (bgMode === 'replace' && !backdrop) ? 'not-allowed' : 'pointer',
                      }}
                    >
                      {spinning('background') ? (
                        <>
                          <Spin /> Processing…
                        </>
                      ) : (
                        <>
                          {bgMode === 'green' ? 'Key out background' : 'Composite backdrop'}{' '}
                          <span style={{ opacity: 0.7, fontWeight: 600 }}>· 1 video token</span>
                        </>
                      )}
                    </button>
                  )}
                  {toolErr('background')}
                </div>
              ) : null}
            </div>
          </div>

          {/* FREE upsell card */}
          {locked ? (
            <div
              style={{
                marginTop: 18,
                padding: 18,
                borderRadius: 14,
                background: 'linear-gradient(150deg,var(--hj-pink-tint),transparent 70%),var(--hj-surface-2)',
                border: '1px solid var(--hj-pink)',
                opacity: 1,
              }}
            >
              <div
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                  padding: '4px 10px',
                  borderRadius: 999,
                  background: 'var(--hj-pink)',
                  color: YELLOW_INK,
                  fontSize: 10.5,
                  fontWeight: 800,
                  letterSpacing: '0.04em',
                }}
              >
                HEY JOE PLUS
              </div>
              <h3
                className="font-hj-display"
                style={{ fontWeight: 700, fontSize: 17, letterSpacing: '-0.02em', margin: '11px 0 6px', color: 'var(--hj-ink)' }}
              >
                Editing lives in Plus
              </h3>
              <p style={{ margin: 0, fontSize: 12.5, lineHeight: 1.5, color: 'var(--hj-ink-2)' }}>
                Trim, enhance audio, color-grade and swap backgrounds on any lesson video — right here, no re-uploads.
                Plus includes monthly video tokens for the AI tools.
              </p>
              <a
                href="https://plus.heyjoe.io"
                target="_blank"
                rel="noreferrer"
                style={{
                  marginTop: 14,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: 44,
                  borderRadius: 11,
                  background: 'var(--hj-yellow)',
                  color: YELLOW_INK,
                  fontSize: 13.5,
                  fontWeight: 800,
                  textDecoration: 'none',
                }}
              >
                Upgrade to Hey Joe Plus
              </a>
              <p style={{ margin: '9px 0 0', textAlign: 'center', fontSize: 11, color: 'var(--hj-ink-4)' }}>
                Cancel anytime · plus.heyjoe.io
              </p>
            </div>
          ) : null}
        </div>

        {/* ---- footer ---- */}
        <div
          style={{
            flex: 'none',
            padding: '14px 18px',
            borderTop: '1px solid var(--hj-hairline-soft)',
            background: 'var(--hj-surface-1)',
          }}
        >
          {isPlus ? (
            <div>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 11, minHeight: 18 }}>
                {anyEdit ? (
                  <button
                    type="button"
                    onClick={handleRevert}
                    disabled={busy}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 6,
                      background: 'none',
                      border: 'none',
                      color: 'var(--hj-ink-3)',
                      fontFamily: 'inherit',
                      fontSize: 12,
                      fontWeight: 600,
                      cursor: busy ? 'not-allowed' : 'pointer',
                      padding: 0,
                    }}
                  >
                    <RotateCcw size={13} strokeWidth={2} />
                    Revert to original
                  </button>
                ) : null}
                <span style={{ fontSize: 11.5, color: 'var(--hj-ink-4)', marginLeft: 'auto' }}>
                  {anyEdit ? `${editCount} edit${editCount !== 1 ? 's' : ''} pending` : 'No edits yet'}
                </span>
              </div>
              <div style={{ display: 'flex', gap: 10 }}>
                <button
                  type="button"
                  onClick={handleCancel}
                  disabled={busy}
                  style={{
                    flex: 'none',
                    padding: '0 20px',
                    height: 44,
                    borderRadius: 11,
                    border: '1px solid var(--hj-hairline)',
                    background: 'transparent',
                    color: 'var(--hj-ink-2)',
                    fontFamily: 'inherit',
                    fontSize: 13.5,
                    fontWeight: 600,
                    cursor: busy ? 'not-allowed' : 'pointer',
                  }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleApply}
                  disabled={!anyEdit || busy}
                  style={{
                    flex: 1,
                    height: 44,
                    borderRadius: 11,
                    border: 'none',
                    background: anyEdit ? 'var(--hj-yellow)' : 'var(--hj-surface-3)',
                    color: anyEdit ? YELLOW_INK : 'var(--hj-ink-4)',
                    fontFamily: 'inherit',
                    fontSize: 13.5,
                    fontWeight: 800,
                    cursor: anyEdit && !busy ? 'pointer' : 'not-allowed',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 7,
                  }}
                >
                  {activeOp === 'finalize' && busy ? (
                    <>
                      <Spin /> Applying…
                    </>
                  ) : anyEdit ? (
                    'Apply to lesson'
                  ) : (
                    'Apply'
                  )}
                </button>
              </div>
            </div>
          ) : (
            <div style={{ display: 'flex', gap: 10 }}>
              <button
                type="button"
                onClick={onClose}
                style={{
                  flex: 'none',
                  padding: '0 20px',
                  height: 44,
                  borderRadius: 11,
                  border: '1px solid var(--hj-hairline)',
                  background: 'transparent',
                  color: 'var(--hj-ink-3)',
                  fontFamily: 'inherit',
                  fontSize: 13.5,
                  fontWeight: 600,
                  cursor: 'pointer',
                }}
              >
                Maybe later
              </button>
              <a
                href="https://plus.heyjoe.io"
                target="_blank"
                rel="noreferrer"
                style={{
                  flex: 1,
                  height: 44,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 7,
                  borderRadius: 11,
                  background: 'var(--hj-yellow)',
                  color: YELLOW_INK,
                  fontSize: 13.5,
                  fontWeight: 800,
                  textDecoration: 'none',
                }}
              >
                <Lock size={15} strokeWidth={2} />
                Unlock editing
              </a>
            </div>
          )}
        </div>
      </aside>

      {/* credit confirm (reused shared component) */}
      <CreditConfirmDialog />

      {/* runtime PLUS_REQUIRED upsell */}
      <UpgradeModal open={showUpgrade} onOpenChange={setShowUpgrade} trigger="ai_feature" />
    </div>,
    document.body
  );
};

export default VideoEditDrawer;
